import type { Draft } from '@reduxjs/toolkit';
import type { RootState } from '@src/store/store';
import type {
  DashboardState,
  SignupRatePayload,
  DailyDownloadsQuery,
  WidgetSummaryPayload,
  DailyDownloadsPayload,
  CurrentDownloadPayload,
  DownloadsWidgetPayload,
  StoreDownloadsSyncSummary,
} from 'src/store/types/dashboard-types';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

export const initialState: DashboardState = {
  currentDownload: {
    chart: {
      series: [], // no OS breakdown yet
    },
    asOf: null,
    status: 'idle',
    error: '',
  },
  totalDownloads: {
    percent: 0,
    total: 0,
    chart: {
      categories: [],
      series: [],
    },
    asOf: null,
    status: 'idle',
    error: '',
  },
  dailyDownloads: {
    period: 'last30',
    month: null,
    months: [],
    request: { period: 'last30' },
    from: '',
    to: '',
    days: [],
    ios: [],
    android: [],
    totals: { ios: 0, android: 0 },
    reportedUntil: { ios: null, android: null },
    asOf: null,
    status: 'idle',
    error: '',
  },
  signupRate: {
    rate: null,
    signups: 0,
    downloads: 0,
    windowDays: 30,
    asOf: null,
    chart: {
      categories: [],
      series: [],
    },
    status: 'idle',
    error: '',
  },
  totalActiveUsers: {
    percent: 0,
    total: 0,
    chart: {
      categories: [],
      series: [],
    },
    status: 'idle',
    error: '',
  },
};

// ====== Thunks (typed) ======
export const loadTotalActiveUsers = createAsyncThunk<WidgetSummaryPayload>(
  'dashboard/loadTotalActiveUsers',
  async () => {
    const { data } = await api.get<WidgetSummaryPayload>(endpoints.admin.appUsers.activeUsers);
    return data;
  }
);

export const loadCurrentDownload = createAsyncThunk<CurrentDownloadPayload>(
  'dashboard/loadCurrentDownload',
  async () => {
    const { data } = await api.get<CurrentDownloadPayload>(
      endpoints.admin.appMobile.currentDownload
    );
    return data;
  }
);

export const loadTotalDownloads = createAsyncThunk<DownloadsWidgetPayload>(
  'dashboard/loadTotalDownloads',
  async () => {
    const { data } = await api.get<DownloadsWidgetPayload>(
      endpoints.admin.appMobile.totalDownloads
    );
    return data;
  }
);

export const loadDailyDownloads = createAsyncThunk<DailyDownloadsPayload, DailyDownloadsQuery>(
  'dashboard/loadDailyDownloads',
  async ({ period, month }) => {
    const { data } = await api.get<DailyDownloadsPayload>(
      endpoints.admin.appMobile.dailyDownloads,
      { params: { period, month } }
    );
    return data;
  }
);

/** Re-reads both stores since launch; the caller reloads the download widgets after. */
export const resyncStoreDownloads = createAsyncThunk<StoreDownloadsSyncSummary>(
  'dashboard/resyncStoreDownloads',
  async () => {
    const { data } = await api.post<StoreDownloadsSyncSummary>(
      endpoints.admin.appMobile.resyncStoreDownloads
    );
    return data;
  }
);

/** Same window: same period and same month (none = the current one). */
export function isSameDailyDownloadsQuery(a: DailyDownloadsQuery, b: DailyDownloadsQuery): boolean {
  return a.period === b.period && (a.month ?? null) === (b.month ?? null);
}

export const loadSignupRate = createAsyncThunk<SignupRatePayload>(
  'dashboard/loadSignupRate',
  async () => {
    const { data } = await api.get<SignupRatePayload>(endpoints.admin.appMobile.signupRate);
    return data;
  }
);

// other thunks…

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // current download
    builder
      .addCase(loadCurrentDownload.pending, (dashboardState: Draft<DashboardState>) => {
        dashboardState.currentDownload.status = 'loading';
        dashboardState.currentDownload.error = '';
      })
      .addCase(loadCurrentDownload.fulfilled, (dashboardState: Draft<DashboardState>, a) => {
        dashboardState.currentDownload.status = 'succeeded';
        dashboardState.currentDownload.chart = a.payload.chart;
        dashboardState.currentDownload.asOf = a.payload.asOf ?? null;
      })
      .addCase(loadCurrentDownload.rejected, (dashboardState: Draft<DashboardState>, action) => {
        dashboardState.currentDownload.status = 'failed';
        dashboardState.currentDownload.error =
          action.error?.message ?? 'Failed to load current download';
      });

    // total downloads
    builder
      .addCase(loadTotalDownloads.pending, (dashboardState: Draft<DashboardState>) => {
        dashboardState.totalDownloads.status = 'loading';
        dashboardState.totalDownloads.error = '';
      })
      .addCase(loadTotalDownloads.fulfilled, (dashboardState: Draft<DashboardState>, a) => {
        dashboardState.totalDownloads.status = 'succeeded';
        dashboardState.totalDownloads.percent = a.payload.percent;
        dashboardState.totalDownloads.total = a.payload.total;
        dashboardState.totalDownloads.chart = a.payload.chart;
        dashboardState.totalDownloads.asOf = a.payload.asOf ?? null;
      })
      .addCase(
        loadTotalDownloads.rejected,
        (
          dashboardState: Draft<DashboardState>,
          a: ReturnType<typeof loadTotalDownloads.rejected>
        ) => {
          dashboardState.totalDownloads.status = 'failed';
          dashboardState.totalDownloads.error =
            a.error?.message ?? 'Failed to load total downloads';
        }
      );

    // daily downloads (KAN-757)
    builder
      .addCase(loadDailyDownloads.pending, (dashboardState: Draft<DashboardState>, a) => {
        dashboardState.dailyDownloads.status = 'loading';
        dashboardState.dailyDownloads.error = '';
        dashboardState.dailyDownloads.request = a.meta.arg;
      })
      .addCase(loadDailyDownloads.fulfilled, (dashboardState: Draft<DashboardState>, a) => {
        // A slower response for the window the user already switched away from
        // must not overwrite the one now on screen.
        if (!isSameDailyDownloadsQuery(a.meta.arg, dashboardState.dailyDownloads.request)) return;
        dashboardState.dailyDownloads = {
          ...a.payload,
          request: a.meta.arg,
          status: 'succeeded',
          error: '',
        };
      })
      .addCase(
        loadDailyDownloads.rejected,
        (
          dashboardState: Draft<DashboardState>,
          a: ReturnType<typeof loadDailyDownloads.rejected>
        ) => {
          if (!isSameDailyDownloadsQuery(a.meta.arg, dashboardState.dailyDownloads.request)) return;
          dashboardState.dailyDownloads.status = 'failed';
          dashboardState.dailyDownloads.error =
            a.error?.message ?? 'Failed to load daily downloads';
        }
      );

    // signup rate (KAN-757)
    builder
      .addCase(loadSignupRate.pending, (dashboardState: Draft<DashboardState>) => {
        dashboardState.signupRate.status = 'loading';
        dashboardState.signupRate.error = '';
      })
      .addCase(loadSignupRate.fulfilled, (dashboardState: Draft<DashboardState>, a) => {
        dashboardState.signupRate = { ...a.payload, status: 'succeeded', error: '' };
      })
      .addCase(
        loadSignupRate.rejected,
        (dashboardState: Draft<DashboardState>, a: ReturnType<typeof loadSignupRate.rejected>) => {
          dashboardState.signupRate.status = 'failed';
          dashboardState.signupRate.error = a.error?.message ?? 'Failed to load signup rate';
        }
      );

    // total active users
    builder
      .addCase(loadTotalActiveUsers.pending, (dashboardState: Draft<DashboardState>) => {
        dashboardState.totalActiveUsers.status = 'loading';
        dashboardState.totalActiveUsers.error = '';
      })
      .addCase(loadTotalActiveUsers.fulfilled, (dashboardState: Draft<DashboardState>, a) => {
        dashboardState.totalActiveUsers.status = 'succeeded';
        dashboardState.totalActiveUsers.percent = a.payload.percent;
        dashboardState.totalActiveUsers.total = a.payload.total;
        dashboardState.totalActiveUsers.chart = a.payload.chart;
      })
      .addCase(
        loadTotalActiveUsers.rejected,
        (
          dashboardState: Draft<DashboardState>,
          a: ReturnType<typeof loadTotalActiveUsers.rejected>
        ) => {
          dashboardState.totalActiveUsers.status = 'failed';
          dashboardState.totalActiveUsers.error =
            a.error?.message ?? 'Failed to load total active users';
        }
      );
  },
});

export default slice.reducer;

export const selectCurrentDownload = (s: RootState) => s.dashboard.currentDownload;
export const selectTotalDownloads = (s: RootState) => s.dashboard.totalDownloads;
export const selectDailyDownloads = (s: RootState) => s.dashboard.dailyDownloads;
export const selectSignupRate = (s: RootState) => s.dashboard.signupRate;
export const selectTotalActiveUsers = (s: RootState) => s.dashboard.totalActiveUsers;
